// AdminCuratedPanel — staff queue for curated-purchase requests
// (migration 0053). Lives as a tab in AdminPanel.
//
// Two surfaces:
//   queue  — filterable list (default: open statuses, includes
//            submitted/priced/purchased/invoiced/paid).
//   detail — full request + state-transition controls + credential
//            reveal (audit-logged) + receipt upload + reject UI.
//
// State machine the UI walks the manager through:
//   submitted → priced (set final total)
//   priced → purchased (mark bought, optionally attach receipt)
//   purchased → invoiced (issue invoice — pick wallet for crypto OR
//              paste RUB payment link)
//   invoiced → paid (manager confirms funds received)
//   paid → completed (close, purges credentials)
//   any pre-completion → rejected (purges credentials)
//
// RUB invoice re-issue: while in 'invoiced' with an expired link, the
// invoice form is re-shown so the manager can paste a fresh link.

import { useState, useEffect, useMemo } from "react";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Btn, Inp, Mono, Serif } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT } from "../i18n.jsx";

const STATUS_TONES = {
  submitted: T_NEW.ink56,
  priced: T_NEW.accent,
  purchased: T_NEW.accent,
  invoiced: T_NEW.persimmon,
  paid: T_NEW.success,
  completed: T_NEW.success,
  cancelled: T_NEW.ink38,
  rejected: T_NEW.danger,
};

const usd = (cents) => `$${(cents / 100).toFixed(2)}`;

export function AdminCuratedPanel({ user }) {
  const t = useT();
  const [view, setView] = useState("queue");
  const [activeId, setActiveId] = useState(null);
  const [filter, setFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    try { setRows(await api.adminListCurated(filter || undefined)); }
    catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);

  if (view === "detail" && activeId) {
    return <AdminDetailView id={activeId} user={user}
                            onBack={() => { setView("queue"); load(); }} />;
  }
  if (showSettings) {
    return <AdminSettingsPanel user={user}
                               onClose={() => setShowSettings(false)} />;
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 14, gap: 10, flexWrap: "wrap" }}>
        <Serif size={20} color={T_NEW.ink} style={{ fontWeight: 600 }}>
          {t("admin.curated.title")}
        </Serif>
        <Btn variant="ghost" onClick={() => setShowSettings(true)}>{t("admin.curated.settings")}</Btn>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
                style={{ padding: "8px 10px", borderRadius: 6,
                         border: `1px solid ${T_NEW.ink12}` }}>
          <option value="">{t("admin.curated.filter.open")}</option>
          <option value="submitted">{t("admin.curated.filter.submitted")}</option>
          <option value="priced">{t("admin.curated.filter.priced")}</option>
          <option value="purchased">{t("admin.curated.filter.purchased")}</option>
          <option value="invoiced">{t("admin.curated.filter.invoiced")}</option>
          <option value="paid">{t("admin.curated.filter.paid")}</option>
          <option value="completed">{t("admin.curated.filter.completed")}</option>
          <option value="cancelled">{t("admin.curated.filter.cancelled")}</option>
          <option value="rejected">{t("admin.curated.filter.rejected")}</option>
        </select>
        <Btn variant="ghost" onClick={load}>{t("admin.curated.refresh")}</Btn>
        <Mono color={T_NEW.ink56} size={T_NEW.micro}>{t("admin.curated.count", { n: rows.length })}</Mono>
      </div>

      {loading
        ? <div style={{ padding: 30, textAlign: "center", color: T_NEW.ink56 }}>{t("admin.curated.loading")}</div>
        : rows.length === 0
          ? <Serif size={14} color={T_NEW.ink56} style={{ padding: 20 }}>{t("admin.curated.empty")}</Serif>
          : rows.map((r) => (
              <button key={r.id} type="button"
                      onClick={() => { setActiveId(r.id); setView("detail"); }}
                      style={{ display: "block", width: "100%", textAlign: "left",
                               padding: 12, marginBottom: 8,
                               background: T_NEW.paperRaised,
                               border: `1px solid ${T_NEW.ink12}`,
                               borderRadius: T_NEW.r2, cursor: "pointer",
                               fontFamily: T_NEW.fontSans }}>
                <div style={{ display: "flex", justifyContent: "space-between",
                              alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <Mono size={13} style={{ fontWeight: 700 }}>
                      {r.resellerNickname ? `@${r.resellerNickname}` : r.resellerEmail}
                    </Mono>
                    <div style={{ fontSize: 12, color: T_NEW.ink56, marginTop: 2 }}>
                      {t("admin.curated.row.items", { n: r.itemCount, stated: usd(r.statedTotalCents) })}
                      {r.finalTotalCents != null && t("admin.curated.row.final", { final: usd(r.finalTotalCents) })}
                      {" · "}{r.preferredCurrency}
                      {" · "}{r.requestType === "account_access" ? t("admin.curated.row.account") : t("admin.curated.row.links")}
                    </div>
                  </div>
                  <span style={{ fontSize: 12,
                                 color: STATUS_TONES[r.status] || T_NEW.ink,
                                 fontWeight: 600,
                                 textTransform: "uppercase",
                                 letterSpacing: 0.5 }}>
                    {r.status}
                  </span>
                </div>
              </button>
            ))
      }
    </div>
  );
}

// ─── Admin detail view with state-transition controls ────────────────
function AdminDetailView({ id, user, onBack }) {
  const toast = useToast();
  const t = useT();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(null);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [settings, setSettings] = useState(null);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const [req, s] = await Promise.all([
        api.adminGetCurated(id),
        api.adminCuratedSettings(),
      ]);
      setR(req); setSettings(s);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]);

  const reveal = async () => {
    try {
      const r = await api.adminRevealCuratedCredentials(id);
      setRevealed(r);
    } catch (e) { toast.error(e.message); }
  };

  if (loading || !r) return (
    <div style={{ padding: 30, textAlign: "center", color: T_NEW.ink56 }}>{t("admin.curated.loading")}</div>
  );

  return (
    <div>
      <button type="button" onClick={onBack}
              style={{ background: "none", border: 0, color: T_NEW.ink56,
                       cursor: "pointer", marginBottom: 14, fontSize: 13 }}>
        {t("admin.curated.back")}
      </button>

      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <div>
          <Mono size={14} style={{ fontWeight: 700 }}>
            {r.resellerNickname ? `@${r.resellerNickname}` : r.resellerEmail}
          </Mono>
          <div style={{ fontSize: 12, color: T_NEW.ink56 }}>
            {r.resellerEmail} · {t("admin.curated.created", { when: new Date(r.createdAt * 1000).toLocaleString() })}
          </div>
        </div>
        <span style={{ fontSize: 13, fontWeight: 700,
                       color: STATUS_TONES[r.status] || T_NEW.ink,
                       textTransform: "uppercase", letterSpacing: 0.6 }}>
          {r.status}
        </span>
      </div>

      {/* Request facts */}
      <div style={{ padding: 14, background: T_NEW.paperSoft,
                    border: `1px solid ${T_NEW.ink12}`,
                    borderRadius: T_NEW.r2, marginBottom: 14 }}>
        <DefList>
          <DefRow k={t("admin.curated.facts.type")} v={r.requestType} />
          {r.siteUrl && <DefRow k={t("admin.curated.facts.site")} v={r.siteUrl} link />}
          <DefRow k={t("admin.curated.facts.recipient")} v={r.deliveryName} />
          <DefRow k={t("admin.curated.facts.address")} v={r.deliveryAddress} />
          <DefRow k={t("admin.curated.facts.items")} v={r.itemCount} />
          <DefRow k={t("admin.curated.facts.stated")} v={usd(r.statedTotalCents)} />
          {r.finalTotalCents != null && <DefRow k={t("admin.curated.facts.final")} v={usd(r.finalTotalCents)} />}
          <DefRow k={t("admin.curated.facts.currency")} v={r.preferredCurrency} />
          {r.resellerNotes && <DefRow k={t("admin.curated.facts.notes")} v={r.resellerNotes} />}
          {Array.isArray(r.productLinks) && r.productLinks.length > 0 && (
            <DefRow k={t("admin.curated.facts.links")} v={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {r.productLinks.map((l, i) =>
                  <li key={i}><a href={l.url} target="_blank" rel="noopener noreferrer"
                                 style={{ color: T_NEW.accent }}>{l.url}</a> × {l.qty}
                    {l.notes ? ` — ${l.notes}` : ""}</li>,
                )}
              </ul>} />
          )}
        </DefList>
      </div>

      {/* Credentials block */}
      {r.requestType === "account_access" && (
        <div style={{ padding: 14,
                      background: revealed ? `${T_NEW.persimmon}10` : T_NEW.paperRaised,
                      border: `1px solid ${revealed ? T_NEW.persimmon + "55" : T_NEW.ink12}`,
                      borderRadius: T_NEW.r2, marginBottom: 14 }}>
          <Mono size={T_NEW.micro} color={T_NEW.ink56}
                style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            {t("admin.curated.creds.title")}
          </Mono>
          {r.credentialsPurgedAt
            ? <Serif size={13} color={T_NEW.ink56}>{t("admin.curated.creds.purged")}</Serif>
            : !r.hasCredentials
              ? <Serif size={13} color={T_NEW.ink56}>{t("admin.curated.creds.none")}</Serif>
              : revealed
                ? (
                  <div style={{ fontFamily: T_NEW.fontMono, fontSize: 13 }}>
                    <div><strong>{t("admin.curated.creds.login")}</strong> {revealed.login}</div>
                    <div><strong>{t("admin.curated.creds.password")}</strong> {revealed.password}</div>
                    <div style={{ fontSize: 11, color: T_NEW.danger, marginTop: 6 }}>
                      {t("admin.curated.creds.audited")}
                    </div>
                  </div>
                )
                : <Btn variant="ghost" onClick={reveal}>{t("admin.curated.creds.reveal")}</Btn>
          }
        </div>
      )}

      {/* State transition controls */}
      {r.status === "submitted" && <PriceForm id={id} stated={r.statedTotalCents} onDone={load} />}
      {r.status === "priced" && <PurchasedForm id={id} onDone={load} />}
      {(r.status === "purchased" || (r.status === "invoiced" && r.invoiceExpired)) && (
        <InvoiceForm id={id} r={r} settings={settings}
                     reissue={r.status === "invoiced"} onDone={load} />
      )}
      {r.status === "invoiced" && !r.invoiceExpired && (
        <div style={{ padding: 14, background: `${T_NEW.persimmon}10`,
                      border: `1px solid ${T_NEW.persimmon}44`,
                      borderRadius: T_NEW.r2, marginBottom: 14 }}>
          <Mono size={13} style={{ fontWeight: 600, marginBottom: 6 }}>
            {t("admin.curated.invoice.waiting", { amount: parseFloat(r.invoiceAmount).toFixed(2), currency: r.preferredCurrency })}
          </Mono>
          <div style={{ fontSize: 12, color: T_NEW.ink56, fontFamily: T_NEW.fontMono,
                        wordBreak: "break-all", marginBottom: 10 }}>
            {r.invoicePaymentTarget}
          </div>
          {r.invoiceExpiresAt && (
            <div style={{ fontSize: 11, color: T_NEW.ink38, marginBottom: 8 }}>
              {t("admin.curated.invoice.expires", { when: new Date(r.invoiceExpiresAt * 1000).toLocaleString() })}
            </div>
          )}
          <Btn variant="primary"
               onClick={async () => {
                 try {
                   await api.adminMarkPaidCurated(id);
                   toast.success(t("admin.curated.invoice.markPaid.ok"));
                   await load();
                 } catch (e) { toast.error(e.message); }
               }}>
            {t("admin.curated.invoice.markPaid")}
          </Btn>
        </div>
      )}
      {r.status === "paid" && (
        <Btn variant="primary"
             onClick={async () => {
               if (!confirm(t("admin.curated.complete.confirm"))) return;
               try {
                 await api.adminCompleteCurated(id);
                 toast.success(t("admin.curated.complete.ok"));
                 onBack();
               } catch (e) { toast.error(e.message); }
             }}>
          {t("admin.curated.complete")}
        </Btn>
      )}

      {/* Reject — available across pre-completion states */}
      {["submitted", "priced", "purchased", "invoiced"].includes(r.status) && (
        <div style={{ marginTop: 24, paddingTop: 16,
                      borderTop: `1px solid ${T_NEW.ink12}` }}>
          {showReject
            ? <>
                <Inp label={t("admin.curated.reject.label")}
                     value={rejectReason} onChange={setRejectReason}
                     placeholder={t("admin.curated.reject.placeholder")} />
                <Btn variant="ghost" style={{ color: T_NEW.danger }}
                     onClick={async () => {
                       if (!rejectReason.trim()) { toast.error(t("admin.curated.reject.required")); return; }
                       try {
                         await api.adminRejectCurated(id, rejectReason.trim());
                         toast.success(t("admin.curated.reject.ok"));
                         onBack();
                       } catch (e) { toast.error(e.message); }
                     }}>{t("admin.curated.reject.confirm")}</Btn>
                <Btn variant="ghost" onClick={() => { setShowReject(false); setRejectReason(""); }}>
                  {t("admin.curated.reject.cancel")}
                </Btn>
              </>
            : <Btn variant="ghost" onClick={() => setShowReject(true)} style={{ color: T_NEW.danger }}>
                {t("admin.curated.reject.start")}
              </Btn>}
        </div>
      )}
    </div>
  );
}

function PriceForm({ id, stated, onDone }) {
  const toast = useToast();
  const t = useT();
  const [total, setTotal] = useState((stated / 100).toFixed(2));
  const [notes, setNotes] = useState("");
  return (
    <div style={{ padding: 14, background: T_NEW.paperRaised,
                  border: `1px solid ${T_NEW.accent}55`,
                  borderRadius: T_NEW.r2, marginBottom: 14 }}>
      <Mono size={13} style={{ fontWeight: 600, marginBottom: 8 }}>
        {t("admin.curated.price.title")}
      </Mono>
      <Inp value={total} onChange={setTotal} inputMode="decimal" placeholder="120.00" />
      <Inp label={t("admin.curated.price.notes")} value={notes} onChange={setNotes}
           placeholder={t("admin.curated.price.notes.placeholder")} />
      <Btn variant="primary"
           onClick={async () => {
             const cents = Math.round(parseFloat(total) * 100);
             if (!cents) { toast.error(t("admin.curated.price.invalid")); return; }
             try {
               await api.adminPriceCurated(id, cents, notes.trim() || undefined);
               toast.success(t("admin.curated.price.ok"));
               await onDone();
             } catch (e) { toast.error(e.message); }
           }}>
        {t("admin.curated.price.save")}
      </Btn>
    </div>
  );
}

function PurchasedForm({ id, onDone }) {
  const toast = useToast();
  const t = useT();
  const [receipt, setReceipt] = useState("");
  const [uploading, setUploading] = useState(false);
  const onPick = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const up = await api.uploadImage(file);
      setReceipt(up.path);
      toast.success(t("admin.curated.purchased.uploaded"));
    } catch (e) { toast.error(e.message); }
    setUploading(false);
  };
  return (
    <div style={{ padding: 14, background: T_NEW.paperRaised,
                  border: `1px solid ${T_NEW.accent}55`,
                  borderRadius: T_NEW.r2, marginBottom: 14 }}>
      <Mono size={13} style={{ fontWeight: 600, marginBottom: 8 }}>
        {t("admin.curated.purchased.title")}
      </Mono>
      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif"
             onChange={(e) => onPick(e.target.files?.[0])}
             style={{ marginBottom: 10 }} />
      {receipt && <div style={{ fontSize: 12, color: T_NEW.ink56, marginBottom: 10 }}>
        {t("admin.curated.purchased.receipt")} {receipt}
      </div>}
      <Btn variant="primary" disabled={uploading}
           onClick={async () => {
             try {
               await api.adminPurchasedCurated(id, receipt || undefined);
               toast.success(t("admin.curated.purchased.ok"));
               await onDone();
             } catch (e) { toast.error(e.message); }
           }}>
        {t("admin.curated.purchased.mark")}
      </Btn>
    </div>
  );
}

function InvoiceForm({ id, r, settings, reissue, onDone }) {
  const toast = useToast();
  const t = useT();
  const [currency, setCurrency] = useState(r.preferredCurrency);
  const [paymentTarget, setPaymentTarget] = useState("");
  const [pickedWallet, setPickedWallet] = useState("");

  const wallets = useMemo(() => (settings?.cryptoWallets || []).filter((w) => w.currency === currency), [settings, currency]);

  // Auto-fill from picked wallet (crypto path).
  useEffect(() => {
    if (currency !== "RUB" && pickedWallet) {
      setPaymentTarget(pickedWallet);
    }
  }, [pickedWallet, currency]);

  const previewAmount = currency === "RUB"
    ? `${((r.finalTotalCents / 100) * (settings?.rubPerUsd || 100)).toFixed(2)} ₽`
    : `${(r.finalTotalCents / 100).toFixed(2)} ${currency}`;

  return (
    <div style={{ padding: 14, background: T_NEW.paperRaised,
                  border: `1px solid ${T_NEW.accent}55`,
                  borderRadius: T_NEW.r2, marginBottom: 14 }}>
      <Mono size={13} style={{ fontWeight: 600, marginBottom: 8 }}>
        {reissue ? t("admin.curated.invoice.reissue") : t("admin.curated.invoice.title")}
      </Mono>
      <Mono size={11} color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
        {t("admin.curated.invoice.currency")}
      </Mono>
      <select value={currency} onChange={(e) => { setCurrency(e.target.value); setPaymentTarget(""); setPickedWallet(""); }}
              style={{ width: "100%", marginBottom: 10,
                       padding: "10px 12px", borderRadius: 6,
                       border: `1px solid ${T_NEW.ink12}` }}>
        <option value="USDT">USDT</option>
        <option value="USDC">USDC</option>
        <option value="RUB">RUB</option>
      </select>

      {currency !== "RUB" && (
        <>
          <Mono size={11} color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
            {t("admin.curated.invoice.wallet")}
          </Mono>
          <select value={pickedWallet} onChange={(e) => setPickedWallet(e.target.value)}
                  style={{ width: "100%", marginBottom: 10,
                           padding: "10px 12px", borderRadius: 6,
                           border: `1px solid ${T_NEW.ink12}` }}>
            <option value="">{t("admin.curated.invoice.wallet.placeholder")}</option>
            {wallets.map((w) =>
              <option key={w.address} value={w.address}>
                {w.network} · {w.label || w.address.slice(0, 16) + "…"}
              </option>,
            )}
          </select>
        </>
      )}

      <Inp label={currency === "RUB" ? t("admin.curated.invoice.target.rub") : t("admin.curated.invoice.target.crypto")}
           value={paymentTarget} onChange={setPaymentTarget}
           placeholder={currency === "RUB" ? t("admin.curated.invoice.placeholder.rub") : t("admin.curated.invoice.placeholder.crypto")} />

      <div style={{ fontSize: 12, color: T_NEW.ink56, marginBottom: 10 }}>
        {t("admin.curated.invoice.preview")}<strong>{previewAmount}</strong>
        {currency === "RUB" && <>{t("admin.curated.invoice.previewExpires")}</>}
      </div>

      <Btn variant="primary"
           onClick={async () => {
             if (!paymentTarget.trim()) { toast.error(t("admin.curated.invoice.required")); return; }
             try {
               await api.adminInvoiceCurated(id, paymentTarget.trim(), currency);
               toast.success(reissue ? t("admin.curated.invoice.reissued") : t("admin.curated.invoice.ok"));
               await onDone();
             } catch (e) { toast.error(e.message); }
           }}>
        {reissue ? t("admin.curated.invoice.resend") : t("admin.curated.invoice.send")}
      </Btn>
    </div>
  );
}

// ─── Admin settings (wallets + RUB rate) ─────────────────────────────
function AdminSettingsPanel({ user, onClose }) {
  const toast = useToast();
  const t = useT();
  const [s, setS] = useState(null);
  // Curated FX is computed: effective = base × (1 + markupPct/100).
  // base is the CBR market rate (auto-refreshed hourly by the retail
  // FX updater — we reuse the same source instead of polling twice).
  // markupPct is the curated-only margin; admin sets this, base is
  // derived on every save so it stays current with CBR.
  const [marketRate, setMarketRate] = useState(null); // pulled from /admin/settings/fx
  const [marketUpdatedAt, setMarketUpdatedAt] = useState(null);
  const [markupPct, setMarkupPct] = useState("");
  const [paymentInstructions, setPaymentInstructions] = useState("");
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const isAdmin = user?.role === "admin";

  const effectiveRate = useMemo(() => {
    const base = parseFloat(marketRate);
    const m = parseFloat(markupPct);
    if (!base || isNaN(base)) return null;
    if (isNaN(m)) return base;
    return base * (1 + m / 100);
  }, [marketRate, markupPct]);

  // Back-compute markupPct from the stored rubPerUsd + current market
  // rate. Lets us migrate from "raw rate" storage without a server
  // change — admin sees the % they're actually charging vs the market.
  const deriveMarkupFromStored = (storedRate, market) => {
    const r = parseFloat(storedRate);
    const m = parseFloat(market);
    if (!r || !m) return "";
    const pct = ((r / m) - 1) * 100;
    return pct.toFixed(2);
  };

  const loadAll = async () => {
    try {
      const [got, fx] = await Promise.all([
        api.adminCuratedSettings(),
        api.getFxSettings().catch(() => null),
      ]);
      setS(got);
      const baseRate = fx?.base ?? null;
      setMarketRate(baseRate);
      setMarketUpdatedAt(fx?.auto?.lastChangedAt || null);
      setMarkupPct(deriveMarkupFromStored(got.rubPerUsd, baseRate));
      setPaymentInstructions(got.paymentInstructions || "");
      setWallets(got.cryptoWallets || []);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const refreshFromCbr = async () => {
    setRefreshing(true);
    try {
      const fx = await api.refreshFxFromCbr();
      setMarketRate(fx?.base ?? null);
      setMarketUpdatedAt(fx?.auto?.lastChangedAt || null);
      toast.success(t("admin.curated.settings.refreshedToast"));
    } catch (e) { toast.error(e.message); }
    setRefreshing(false);
  };

  const addWallet = () => setWallets((w) => [...w, { currency: "USDT", network: "TRC20", address: "", label: "" }]);
  const updateWallet = (i, k, v) => setWallets((w) => w.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const removeWallet = (i) => setWallets((w) => w.filter((_, idx) => idx !== i));

  const save = async () => {
    try {
      // We store the effective rate (RUB/USD) the server quotes to
      // resellers. Base + markup live only in the UI state; on next
      // load we re-derive markupPct from the stored rate + current
      // market rate. This keeps the server schema unchanged.
      await api.adminUpdateCuratedSettings({
        rubPerUsd: effectiveRate ?? undefined,
        cryptoWallets: wallets,
        paymentInstructions,
      });
      toast.success(t("admin.curated.settings.saved"));
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div style={{ padding: 30, textAlign: "center", color: T_NEW.ink56 }}>{t("admin.curated.loading")}</div>;

  return (
    <div>
      {/* Back link sits on its own row above the heading — the
          previous layout overlapped them because Serif renders large
          inline. */}
      <div style={{ marginBottom: 8 }}>
        <button type="button" onClick={onClose}
                style={{ background: "none", border: 0, color: T_NEW.ink56,
                         cursor: "pointer", fontSize: 13, padding: 0 }}>
          {t("admin.curated.settings.back")}
        </button>
      </div>
      <Serif size={20} color={T_NEW.ink}
             style={{ display: "block", fontWeight: 600, marginBottom: 18 }}>
        {t("admin.curated.settings.title")}
      </Serif>

      {/* FX block — base (CBR) × markup% = effective. Saved value is
          the effective rate; markup% is back-derived on load. */}
      <div style={{ padding: 14, marginBottom: 16,
                    background: T_NEW.paperSoft,
                    border: `1px solid ${T_NEW.ink12}`,
                    borderRadius: T_NEW.r2 }}>
        <Mono size={T_NEW.micro} color={T_NEW.ink56}
              style={{ display: "block", marginBottom: 10, fontWeight: 600,
                       letterSpacing: 0.5 }}>
          {t("admin.curated.settings.fxTitle")}
        </Mono>

        <div style={{ display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: 10, marginBottom: 10 }}>
          <div>
            <Mono size={11} color={T_NEW.ink56}
                  style={{ display: "block", marginBottom: 4 }}>
              {t("admin.curated.settings.cbrBase")}
            </Mono>
            <div style={{ padding: "10px 12px",
                          background: T_NEW.paper,
                          border: `1px solid ${T_NEW.ink12}`,
                          borderRadius: T_NEW.r1,
                          fontFamily: T_NEW.fontMono, fontSize: 15,
                          color: marketRate ? T_NEW.ink : T_NEW.ink38 }}>
              {marketRate ? parseFloat(marketRate).toFixed(2) : "—"}
            </div>
          </div>
          <div>
            <Mono size={11} color={T_NEW.ink56}
                  style={{ display: "block", marginBottom: 4 }}>
              {t("admin.curated.settings.markup")}
            </Mono>
            <input value={markupPct}
                   onChange={(e) => setMarkupPct(e.target.value)}
                   inputMode="decimal" placeholder="5"
                   style={{ width: "100%", boxSizing: "border-box",
                            padding: "10px 12px",
                            background: T_NEW.paper, color: T_NEW.ink,
                            border: `1px solid ${T_NEW.ink12}`,
                            borderRadius: T_NEW.r1,
                            fontFamily: T_NEW.fontMono, fontSize: 15 }} />
          </div>
          <div>
            <Mono size={11} color={T_NEW.ink56}
                  style={{ display: "block", marginBottom: 4 }}>
              {t("admin.curated.settings.effective")}
            </Mono>
            <div style={{ padding: "10px 12px",
                          background: `${T_NEW.accent}10`,
                          border: `1.5px solid ${T_NEW.accent}`,
                          borderRadius: T_NEW.r1,
                          fontFamily: T_NEW.fontMono, fontSize: 15,
                          fontWeight: 600,
                          color: effectiveRate ? T_NEW.ink : T_NEW.ink38 }}>
              {effectiveRate ? effectiveRate.toFixed(2) : "—"}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center",
                      gap: 10, flexWrap: "wrap" }}>
          <Btn variant="ghost" disabled={refreshing} onClick={refreshFromCbr}>
            {refreshing ? t("admin.curated.settings.refreshing") : t("admin.curated.settings.refreshCbr")}
          </Btn>
          {marketUpdatedAt && (
            <Mono size={11} color={T_NEW.ink56}>
              {t("admin.curated.settings.updatedAt", { when: new Date(marketUpdatedAt * 1000).toLocaleString() })}
            </Mono>
          )}
        </div>
        <div style={{ fontSize: 12, color: T_NEW.ink56,
                      fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                      marginTop: 8, lineHeight: 1.5 }}>
          {t("admin.curated.settings.fxHint")}
        </div>
      </div>

      <Mono size={T_NEW.micro} color={T_NEW.ink56}
            style={{ display: "block", marginBottom: 6 }}>
        {t("admin.curated.settings.payInstructions")}
      </Mono>
      <textarea value={paymentInstructions} onChange={(e) => setPaymentInstructions(e.target.value)}
                rows={3} style={{ width: "100%", marginBottom: 14,
                                  padding: 10, fontSize: 13,
                                  border: `1px solid ${T_NEW.ink12}`,
                                  borderRadius: 6, fontFamily: T_NEW.fontSans }} />

      <Mono size={T_NEW.micro} color={T_NEW.ink56}
            style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
        {t("admin.curated.settings.wallets")}
      </Mono>
      {wallets.map((w, i) => (
        <div key={i} style={{ display: "grid",
                              gridTemplateColumns: "100px 100px 1fr 1fr auto",
                              gap: 6, marginBottom: 6, alignItems: "center" }}>
          <select value={w.currency} onChange={(e) => updateWallet(i, "currency", e.target.value)}
                  style={{ padding: 8, border: `1px solid ${T_NEW.ink12}`, borderRadius: 4 }}>
            <option>USDT</option>
            <option>USDC</option>
          </select>
          <Inp value={w.network} onChange={(v) => updateWallet(i, "network", v)} placeholder="TRC20" />
          <Inp value={w.address} onChange={(v) => updateWallet(i, "address", v)} placeholder={t("admin.curated.settings.wallet.address")} />
          <Inp value={w.label || ""} onChange={(v) => updateWallet(i, "label", v)} placeholder={t("admin.curated.settings.wallet.label")} />
          <button type="button" onClick={() => removeWallet(i)}
                  style={{ background: "none", border: 0, color: T_NEW.danger,
                           cursor: "pointer", padding: 6 }}>×</button>
        </div>
      ))}
      <Btn variant="ghost" onClick={addWallet}>{t("admin.curated.settings.addWallet")}</Btn>
      <div style={{ height: 16 }} />
      <Btn variant="primary" disabled={!isAdmin} onClick={save}>
        {isAdmin ? t("admin.curated.settings.save") : t("admin.curated.settings.adminOnly")}
      </Btn>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────
function DefList({ children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "140px 1fr",
                  gap: "6px 12px", fontSize: 13, lineHeight: 1.55 }}>
      {children}
    </div>
  );
}
function DefRow({ k, v, link }) {
  return (
    <>
      <div style={{ color: T_NEW.ink56, fontFamily: T_NEW.fontMono, fontSize: 11 }}>{k}</div>
      <div style={{ color: T_NEW.ink, wordBreak: "break-word" }}>
        {link && typeof v === "string" && /^https?:\/\//.test(v)
          ? <a href={v} target="_blank" rel="noopener noreferrer"
               style={{ color: T_NEW.accent }}>{v}</a>
          : v}
      </div>
    </>
  );
}
